import { Listbox, ListboxButton, ListboxOption, ListboxOptions, type ListboxProps } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import { ChevronDown, Search } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Field, { type FieldProps } from '@/components/common/Field';
import InputGroup from '@/components/common/Input/InputGroup';

export const defaultRenderOption = (option: SelectOption) => (
    <>
        <span className="block truncate">{option.label}</span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-teal-600 group-data-[focus]:text-white group-[&:not([data-selected])]:hidden">
            <CheckIcon aria-hidden="true" className="size-5" />
        </span>
    </>
);

export interface SelectOption {
    value: string | number | null;
    label: string;
    [key: string]: any;
}

interface BaseProps extends Omit<FieldProps, 'id' | 'children'> {
    id?: string;
    options: SelectOption[];
    inputState?: 'valid' | 'invalid' | 'normal';
    renderOption?: (option: SelectOption) => ReactNode;
    name: string;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
}

type ModeProps =
    | { multiple?: false; value?: SelectOption['value']; onChange?: (value: SelectOption['value']) => void }
    | { multiple: true; value?: SelectOption['value'][]; onChange?: (value: SelectOption['value'][]) => void };

export type SelectProps = BaseProps & ModeProps;

const useListBox = (props: ModeProps): ListboxProps<any, SelectOption['value'] | SelectOption['value'][]> => {
    if (props.multiple) {
        return {
            multiple: true,
            value: props.value,
            onChange: (value: SelectOption['value'][]) => props.onChange && props.onChange(value),
        };
    }

    return {
        value: props.value,
        onChange: (value: SelectOption['value']) => props.onChange && props.onChange(value),
    };
};

const Select = ({
    id,
    label,
    options,
    renderOption = defaultRenderOption,
    helpElement,
    inputState = 'normal',
    errorElement,
    name,
    placeholder,
    disabled = false,
    required,
    searchable = false,
    searchPlaceholder = 'Search...',
    ...props
}: SelectProps) => {
    const elementId = useId();
    const computedId = id || elementId;
    const { t } = useTranslation('common');
    const [searchTerm, setSearchTerm] = useState('');

    const listBoxProps = useListBox(props);

    const { value } = props;
    const selectedOptions = useMemo(() => {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return options.filter(option => value.includes(option.value));
        }

        if (!options.find(option => option.value === value)) {
            return [];
        }

        return [options.find(option => option.value === value)!];
    }, [options, value]);

    // Filter options based on search term when searchable is enabled
    const filteredOptions = useMemo(() => {
        if (!searchable || !searchTerm) {
            return options;
        }
        return options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchable, searchTerm]);

    const isPlaceholder = !selectedOptions || selectedOptions.length === 0;

    const optionLabel =
        (selectedOptions.length === 1 && selectedOptions[0].label) ||
        (selectedOptions.length > 1 && selectedOptions.map(option => option.label).join(', ')) ||
        placeholder ||
        (props.multiple ? t('common:select.placeholderMultiple') : t('common:select.placeholderSingle'));

    return (
        <Listbox {...listBoxProps}>
            {({ open }) => (
                <Field
                    name={name}
                    label={label}
                    errorElement={errorElement}
                    helpElement={helpElement}
                    id={computedId}
                    required={required}
                >
                    <ListboxButton
                        as={InputGroup}
                        disabled={disabled}
                        inputState={inputState}
                        data-cy="select"
                        data-cy-id={computedId}
                        data-empty={!selectedOptions.length || undefined}
                        className={clsx(
                            'data-disabled:cursor-not-allowed data-empty:text-neutral-400',
                            disabled ? 'bg-neutral-50 text-neutral-500' : ''
                        )}
                    >
                        <span
                            className={`w-full grow truncate py-[7px] pr-1 text-base font-medium ${isPlaceholder || disabled ? 'text-neutral-400' : 'text-neutral-800'}`}
                        >
                            {optionLabel}
                        </span>
                        {!disabled && (
                            <span className="shrink-0 text-base text-neutral-800 select-none sm:text-sm/6">
                                <ChevronDown
                                    aria-hidden="true"
                                    className={clsx('size-5 transition-transform', open && 'rotate-180')}
                                />
                            </span>
                        )}
                    </ListboxButton>

                    <ListboxOptions
                        anchor="bottom start"
                        className={clsx(
                            // position
                            'absolute z-40 mt-1 max-h-56 overflow-auto',
                            // base
                            'rounded-md bg-white text-base font-medium text-neutral-800 shadow-lg ring-1 ring-black/5 focus:outline-none',
                            // transitions
                            'data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in data-[closed]:data-[leave]:opacity-0'
                        )}
                        data-cy="options"
                        data-cy-id={computedId}
                        transition
                    >
                        {/* Search Input - only show when searchable is true */}
                        {searchable && (
                            <div className="sticky top-0 border-b border-gray-200 bg-white px-3 py-2">
                                <div className="relative">
                                    <input
                                        type="text"
                                        className="w-full rounded-md border border-gray-300 py-1 pr-8 pl-3 text-base font-medium text-neutral-800 placeholder:text-neutral-400 focus:border-transparent focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        autoFocus
                                    />
                                    <Search
                                        className="absolute top-1/2 right-2 size-4 -translate-y-1/2 transform text-neutral-500"
                                        strokeWidth={2}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Options Container */}
                        <div className="max-h-48 overflow-y-auto py-1">
                            {filteredOptions.length === 0 ? (
                                <div className="relative cursor-default px-4 py-2 text-gray-500 select-none">
                                    {searchable && searchTerm ? 'No options found.' : 'No options available.'}
                                </div>
                            ) : (
                                <div className="py-1">
                                    {filteredOptions.map(option => (
                                        <ListboxOption
                                            key={option.value}
                                            value={option.value}
                                            className={clsx(
                                                // base
                                                'group relative cursor-default py-2 pr-9 pl-3 text-base text-gray-900 select-none',
                                                // focus
                                                'data-[focus]:bg-teal-600 data-[focus]:text-white data-[focus]:outline-none',
                                                // selected state
                                                selectedOptions.some(selected => selected.value === option.value) &&
                                                    'bg-teal-50'
                                            )}
                                        >
                                            {renderOption(option)}
                                            {selectedOptions.some(selected => selected.value === option.value) && (
                                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-base text-teal-600">
                                                    <CheckIcon className="size-5" aria-hidden="true" />
                                                </span>
                                            )}
                                        </ListboxOption>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ListboxOptions>
                </Field>
            )}
        </Listbox>
    );
};

export default Select;
